/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import qs from 'query-string';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type { RouterContext } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	createContainer,
	type Action,
	type BoundActions,
	type HookFunction,
	type OverrideContainerComponent,
} from '@atlassian/react-sweet-state';

export const DefaultFilterFields = {
	// sorting
	SortOrder: 'sortOrder',
	SortKey: 'sortKey',

	// pagination
	Page: 'page',
} as const;

type DefaultFilterKey = (typeof DefaultFilterFields)[keyof typeof DefaultFilterFields];
type DefaultInitialState = Record<DefaultFilterKey, string>;
type FilterConfig<T> = {
	getInitialState: ({
		routeQuery,
		isAdminSettingsContext,
	}: {
		routeQuery: RouterContext['query'];
		isAdminSettingsContext: boolean;
	}) => T;
};

export const createUseFilter = <T extends DefaultInitialState>(
	storeName: string,
	{ getInitialState }: FilterConfig<T>,
): {
	useFilter: HookFunction<
		T,
		BoundActions<
			T,
			{
				updateFilter: (
					nameOrFilter: keyof T | Partial<T>,
					value?: string,
				) => Action<
					T,
					{
						onUpdate: (filter: T) => void;
						isAdminSettingsContext: boolean;
					}
				>;
				forceUpdate: () => Action<
					T,
					{
						onUpdate: (filter: T) => void;
						isAdminSettingsContext: boolean;
					}
				>;
			}
		>,
		void
	>;
	useScopedFilter: HookFunction<
		string,
		BoundActions<
			T,
			{
				updateFilter: (
					nameOrFilter: keyof T | Partial<T>,
					value?: string,
				) => Action<
					T,
					{
						onUpdate: (filter: T) => void;
						isAdminSettingsContext: boolean;
					}
				>;
				forceUpdate: () => Action<
					T,
					{
						onUpdate: (filter: T) => void;
						isAdminSettingsContext: boolean;
					}
				>;
			}
		>,
		keyof T
	>;
	FilterContainer: OverrideContainerComponent<{
		onUpdate: (filter: T) => void;
		isAdminSettingsContext: boolean;
	}>;
} => {
	type Name = keyof T;
	type ContainerProps = {
		onUpdate: (filter: T) => void;
		isAdminSettingsContext: boolean;
	};

	const actions = {
		updateFilter:
			(nameOrFilter: keyof T | Partial<T>, value?: string): Action<T, ContainerProps> =>
			({ setState, getState }, { onUpdate }) => {
				const currentState = getState();
				const newFilter =
					typeof nameOrFilter === 'object'
						? nameOrFilter
						: {
								[nameOrFilter]: value,
							};

				const isFilterChanging = Object.keys(newFilter).some(
					// @ts-expect-error - Element implicitly has an 'any'
					(key) => key !== DefaultFilterFields.Page && currentState[key] !== newFilter[key],
				);

				setState({
					...currentState,
					...(isFilterChanging && {
						[DefaultFilterFields.Page]: 1,
					}),
					...newFilter,
				});

				onUpdate(getState());
			},
		forceUpdate:
			(): Action<T, ContainerProps> =>
			({ getState }, { onUpdate }) =>
				onUpdate(getState()),
	};

	type Actions = typeof actions;

	const Store = createStore<T, Actions>({
		// oxlint-disable-next-line typescript/consistent-type-assertions
		initialState: {} as T,
		actions,
		name: storeName,
	});

	const useFilter = createHook(Store);
	const useScopedFilter = createHook(Store, {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		selector: (state, name: Name) => state[name] as string,
	});

	const FilterContainer = createContainer<T, Actions, ContainerProps>(Store, {
		onInit:
			() =>
			({ setState }, { isAdminSettingsContext }) => {
				try {
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					const routeQuery = qs.parse(window.location.search);
					setState(getInitialState({ routeQuery, isAdminSettingsContext }));
				} catch (error) {
					fireErrorAnalytics({
						// oxlint-disable-next-line typescript/consistent-type-assertions
						error: error as Error,
						meta: {
							id: 'createUseFilter',
							packageName: 'jiraDirectoryBaseV3',
							teamName: 'jira-cosmos',
						},
					});
				}
			},
		// reset initial state on unmount
		onCleanup:
			() =>
			({ setState }, { isAdminSettingsContext }) =>
				setState(getInitialState({ routeQuery: {}, isAdminSettingsContext })),
	});

	return { useFilter, useScopedFilter, FilterContainer };
};
